/**
 * Account Table Component
 *
 * Displays search bar, account list table, and pagination controls.
 */

import React from 'react';
import { Select, SelectItem } from '@heroui/react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react';
import { Button } from '@/shared/ui/components/Button';
import { AccountSearchBar } from '@/features/account-search';
import { formatDate, cn } from '@/shared/lib/utils';
import type { Account } from '@/entities/account';
import type { AccountSearchState, AccountSearchData } from '@/features/account-search';

interface AccountTableProps {
  searchState: AccountSearchState;
  searchData: AccountSearchData;
  onEdit: (account: Account) => void;
  onDelete: (account: Account) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const AccountTable: React.FC<AccountTableProps> = ({
  searchState,
  searchData,
  onEdit,
  onDelete,
  isUpdating,
  isDeleting,
}) => {
  const { searchTerm, platform, currentPage, pageSize, setSearchTerm, setPlatform, setCurrentPage, setPageSize } = searchState;
  const { accounts, totalPages, totalElements, isLoading, error } = searchData;

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <AccountSearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedPlatform={platform}
        onPlatformChange={setPlatform}
      />

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center py-8">
          <div className="text-danger">데이터를 불러오는데 실패했습니다.</div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  플랫폼
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  계정명
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  생성일
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수정일
                </th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {accounts.map((account: Account) => (
                <tr key={account.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-3 px-4 text-sm">
                    <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium uppercase">
                      {account.platform}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm font-medium">{account.account_id}</td>
                  <td className="py-3 px-4 text-sm text-gray-500">{formatDate(account.created_at)}</td>
                  <td className="py-3 px-4 text-sm text-gray-500">{formatDate(account.updated_at)}</td>
                  <td className="py-3 px-4 text-sm">
                    <button
                      onClick={() => onEdit(account)}
                      className="text-primary hover:text-primary/80 mr-3 transition-colors"
                      disabled={isUpdating}
                    >
                      수정
                    </button>
                    <button
                      onClick={() => onDelete(account)}
                      className="text-danger hover:text-danger/80 transition-colors"
                      disabled={isDeleting}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && accounts.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
          {/* Page info and size selector */}
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              전체 <span className="font-medium">{totalElements}</span>개 중{' '}
              <span className="font-medium">{currentPage * pageSize + 1}</span>-
              <span className="font-medium">{Math.min((currentPage + 1) * pageSize, totalElements)}</span> 표시
            </div>

            <Select
              selectedKeys={[String(pageSize)]}
              onSelectionChange={(keys) => {
                setPageSize(Number(Array.from(keys)[0]));
                setCurrentPage(0);
              }}
              classNames={{
                trigger: 'bg-white border border-gray-300 h-8 min-h-8',
                value: 'text-sm',
              }}
              className="w-28"
              aria-label="페이지 크기 선택"
            >
              <SelectItem key="5">5개씩</SelectItem>
              <SelectItem key="10">10개씩</SelectItem>
              <SelectItem key="20">20개씩</SelectItem>
              <SelectItem key="50">50개씩</SelectItem>
            </Select>
          </div>

          {/* Page navigation */}
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(0)} disabled={currentPage === 0}>
              <ChevronsLeft className="w-4 h-4" />
            </Button>

            <Button variant="outline" size="sm" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 3) {
                  pageNum = i;
                } else if (currentPage > totalPages - 3) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={cn(
                      'min-w-[32px] h-8 px-2 text-sm rounded transition-colors',
                      currentPage === pageNum
                        ? 'bg-primary text-white font-medium'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    )}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

AccountTable.displayName = 'AccountTable';
